import React, { useEffect, useState, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faArrowUp, faArrowDown, faCoins, faMoneyBillTransfer,
  faUniversity, faTimes, faSpinner, faClock, faCheck, faBan
} from '@fortawesome/free-solid-svg-icons';
import { bankList } from '../../utils/bankList';
import DataTable, { Column, Pagination } from '../../components/shared/DataTable';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader, UiStatsGrid, UiFilterPanel, UiModal, UiButton, UiInput, UiSelect, StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

interface WalletStats {
  balance: number;
  total_credit: number;
  total_debit: number;
  payin_commission: number;
  payin_commission_count: number;
  payout_commission: number;
  payout_commission_count: number;
  parent_spread: number;
  parent_spread_count: number;
  total_withdrawn: number;
  withdrawn_count: number;
  total_frozen: number;
  total_frozen_gross: number;
  frozen_count: number;
  generated_at?: string;
}

interface WalletData {
  wallet: {
    balance: number;
    total_credit: number;
    total_debit: number;
  };
  stats?: WalletStats | null;
  transactions: Array<{
    id: string;
    transaction_type: string;
    amount: number;
    balance_after: number;
    description: string;
    created_at: string;
  }>;
}

interface Withdrawal {
  id: string;
  withdrawal_number: string;
  amount: number;
  status: string;
  currency: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  wallet_address?: string;
  network?: string;
  slip_url?: string;
  note?: string;
  rejected_reason?: string;
  created_at: string;
}

const WITHDRAWAL_STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faClock, label: 'รออนุมัติ' },
  approved: { bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-700 dark:text-blue-400', icon: faCheck, label: 'อนุมัติแล้ว' },
  completed: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheck, label: 'เสร็จสิ้น' },
  rejected: { bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-400', icon: faBan, label: 'ปฏิเสธ' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faTimes, label: 'ยกเลิก' },
};

const TX_CREDIT_TYPES = ['commission', 'payout_commission', 'parent_commission', 'parent_payout_commission', 'refund', 'unfreeze_return'];

const TX_TYPE_META: Record<string, { label: string; color: string }> = {
  commission:               { label: 'Commission',           color: 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400' },
  payout_commission:        { label: 'Payout Comm.',         color: 'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400' },
  parent_commission:        { label: 'Parent Spread',        color: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  parent_payout_commission: { label: 'Parent Payout Spread', color: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  withdrawal:               { label: 'ถอนเงิน',              color: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400' },
  refund:                   { label: 'คืนเงิน',               color: 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400' },
  freeze:                   { label: 'Freeze',               color: 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400' },
  unfreeze_return:          { label: 'คืนเงิน Unfreeze',      color: 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400' },
};

const txIconClasses = (type: string, isCredit: boolean) =>
  isCredit
    ? type === 'parent_commission' || type === 'parent_payout_commission'
      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
      : type === 'unfreeze_return'
        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
        : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
    : type === 'withdrawal'
      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
      : type === 'freeze'
        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300';

const ResellerWalletPage: React.FC = () => {
  const [data, setData] = useState<WalletData | null>(null);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const withdrawFormRef = useRef<HTMLFormElement>(null);
  const [txTypeFilter, setTxTypeFilter] = useState('all');
  const [txStartDate, setTxStartDate] = useState('');
  const [txEndDate, setTxEndDate] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const [txPerPage, setTxPerPage] = useState(20);
  const [currency, setCurrency] = useState<'THB' | 'USDT'>('THB');
  const [usdtPreview, setUsdtPreview] = useState<{
    rate: number;
    fee_percent: number;
    fee_amount: number;
    net_thb: number;
    usdt_amount: number;
  } | null>(null);
  const [usdtLoading, setUsdtLoading] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_name: '',
    wallet_address: '',
    network: 'TRC20',
    note: ''
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async (page = 1, opts?: { perPage?: number; resetFilter?: boolean }) => {
    setLoading(true);
    setLoadError(null);
    try {
      const perPage = opts?.perPage ?? txPerPage;
      const offset = (page - 1) * perPage;
      const params = new URLSearchParams();
      params.append('limit', String(perPage));
      params.append('offset', String(offset));
      if (!opts?.resetFilter) {
        if (txTypeFilter !== 'all') params.append('type', txTypeFilter);
        if (txStartDate) params.append('start_date', txStartDate);
        if (txEndDate) params.append('end_date', txEndDate);
      }

      const [walletRes, wdRes] = await Promise.all([
        merchantApi.get(`/reseller/wallet?${params}`),
        merchantApi.get('/reseller/withdrawals')
      ]);
      setData(walletRes.data.data);
      setWithdrawals(wdRes.data.data || []);
      setTxTotal(walletRes.data.data?.pagination?.total || 0);
      setTxPage(page);
    } catch (error: any) {
      // เคลียร์ข้อมูลเก่า ไม่ให้ยอดค้างบนจอจนเข้าใจผิด
      console.error('Error loading wallet:', error);
      setData(null);
      setWithdrawals([]);
      setTxTotal(0);
      setLoadError(error?.response?.data?.message || error?.message || 'โหลดข้อมูล wallet ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  // USDT rate preview
  const loadUsdtRate = async (amount: string) => {
    if (currency !== 'USDT' || !amount || parseFloat(amount) <= 0) {
      setUsdtPreview(null);
      return;
    }
    setUsdtLoading(true);
    try {
      const res = await merchantApi.get(`/reseller/withdrawals/usdt-rate?amount=${amount}`);
      if (res.data.success && res.data.data.preview) {
        setUsdtPreview(res.data.data.preview);
      }
    } catch {
      setUsdtPreview(null);
    } finally {
      setUsdtLoading(false);
    }
  };

  // Debounce USDT rate fetch when amount changes
  React.useEffect(() => {
    if (currency !== 'USDT' || !form.amount) {
      setUsdtPreview(null);
      return;
    }
    const timer = setTimeout(() => loadUsdtRate(form.amount), 500);
    return () => clearTimeout(timer);
  }, [form.amount, currency]);

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('กรุณาระบุจำนวนเงิน');
      return;
    }
    if (currency === 'THB' && (!form.bank_name || !form.bank_account_number || !form.bank_account_name)) {
      toast.error('กรุณากรอกข้อมูลบัญชีธนาคารให้ครบ');
      return;
    }
    if (currency === 'USDT' && !form.wallet_address) {
      toast.error('กรุณากรอก Wallet Address');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        amount: parseFloat(form.amount),
        currency,
        note: form.note || undefined
      };
      if (currency === 'THB') {
        payload.bank_name = form.bank_name;
        payload.bank_account_number = form.bank_account_number;
        payload.bank_account_name = form.bank_account_name;
      } else {
        payload.wallet_address = form.wallet_address;
        payload.network = form.network;
      }

      await merchantApi.post('/reseller/withdrawals', payload);
      toast.success('สร้างคำขอถอนเงินสำเร็จ!');
      setShowWithdrawForm(false);
      setCurrency('THB');
      setForm({ amount: '', bank_name: '', bank_account_number: '', bank_account_name: '', wallet_address: '', network: 'TRC20', note: '' });
      loadAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id: string) => {
    const ok = await swalConfirm({
      title: 'ยืนยันยกเลิกคำขอถอนเงิน?',
      icon: 'warning',
      confirmText: 'ยกเลิกคำขอ',
      confirmColor: '#ef4444',
    });
    if (!ok) return;
    try {
      await merchantApi.delete(`/reseller/withdrawals/${id}`);
      toast.success('ยกเลิกสำเร็จ');
      loadAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(amount);

  // Full-screen spinner เฉพาะโหลดครั้งแรก (ยังไม่มีข้อมูล) — เปลี่ยนหน้า/ค้นหาใช้ loading ระดับ component แทน
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--theme-primary)' }}></div>
      </div>
    );
  }

  const txFilterActive = txTypeFilter !== 'all' || !!txStartDate || !!txEndDate;

  const walletCards: StatCardDef[] = [
    {
      icon: faWallet,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'Balance (ถอนได้)',
      value: <span className="tabular-nums" style={{ color: 'var(--theme-primary)' }}>฿{formatCurrency(data?.wallet?.balance || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">ยอดคงเหลือที่ถอนได้จริง</span>,
    },
    {
      icon: faCoins,
      iconClassName: '',
      iconStyle: { background: 'var(--theme-primary-light)', color: 'var(--theme-primary)' },
      label: 'Commission รวมทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100 tabular-nums">฿{formatCurrency(data?.wallet?.total_credit || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">รายได้คอมมิชชั่นสะสม (ก่อนถอน)</span>,
    },
    {
      icon: faMoneyBillTransfer,
      iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ถอนแล้ว (Withdrawn)',
      value: <span className="text-gray-800 dark:text-slate-100 tabular-nums">฿{formatCurrency(data?.stats?.total_withdrawn ?? 0)}</span>,
      subValue: (
        <span className="text-gray-400 dark:text-slate-500">
          เงินออกจริง {data?.stats?.withdrawn_count ?? 0} ครั้ง · ไม่รวมอายัด
        </span>
      ),
    },
    {
      icon: faBan,
      iconClassName: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'อายัดอยู่ (Frozen)',
      value: <span className="text-purple-700 dark:text-purple-400 tabular-nums">฿{formatCurrency(data?.stats?.total_frozen ?? 0)}</span>,
      subValue: (
        <span className="text-gray-400 dark:text-slate-500">
          เงินถูกอายัด ยังไม่คืน/ถอน
          {(data?.stats?.total_frozen_gross ?? 0) !== (data?.stats?.total_frozen ?? 0) && (
            <> · อายัดสะสม ฿{formatCurrency(data?.stats?.total_frozen_gross ?? 0)}</>
          )}
        </span>
      ),
    },
  ];

  const commissionCards: StatCardDef[] = [
    {
      icon: faArrowDown,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ค่าคอมฝาก (Payin)',
      value: <span className="text-green-600 dark:text-green-400 tabular-nums">฿{formatCurrency(data?.stats?.payin_commission ?? 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">{(data?.stats?.payin_commission_count ?? 0).toLocaleString()} รายการ</span>,
    },
    {
      icon: faArrowUp,
      iconClassName: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
      label: 'ค่าคอมถอน (Payout)',
      value: <span className="text-sky-600 dark:text-sky-400 tabular-nums">฿{formatCurrency(data?.stats?.payout_commission ?? 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">{(data?.stats?.payout_commission_count ?? 0).toLocaleString()} รายการ</span>,
    },
    {
      icon: faUniversity,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'Sub-Reseller Spread',
      value: <span className="text-amber-600 dark:text-amber-400 tabular-nums">฿{formatCurrency(data?.stats?.parent_spread ?? 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">{(data?.stats?.parent_spread_count ?? 0).toLocaleString()} รายการ</span>,
    },
  ];

  const withdrawalColumns: Column<Withdrawal>[] = [
    {
      key: 'withdrawal_number',
      header: 'เลขที่',
      render: (wd) => <span className="text-sm font-medium text-gray-800 dark:text-slate-100">{wd.withdrawal_number}</span>,
    },
    {
      key: 'amount',
      header: 'จำนวน',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (wd) => (
        <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--theme-accent)' }}>
          ฿{formatCurrency(wd.amount)}
        </span>
      ),
    },
    {
      key: 'account',
      header: 'บัญชี',
      render: (wd) => (
        wd.currency === 'USDT' ? (
          <div className="text-sm text-gray-600 dark:text-slate-300">
            <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 rounded text-xs font-medium">USDT</span>
            <span className="text-xs text-gray-400 dark:text-slate-500 ml-1">{wd.network}</span>
            <div className="text-xs text-gray-400 dark:text-slate-500 font-mono mt-0.5 truncate max-w-[200px]">{wd.wallet_address}</div>
          </div>
        ) : (
          <div className="text-sm text-gray-600 dark:text-slate-300">
            <div>{wd.bank_name}</div>
            <div className="text-xs text-gray-400 dark:text-slate-500">{wd.bank_account_number} - {wd.bank_account_name}</div>
          </div>
        )
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (wd) => (
        <div>
          <StatusBadge
            status={WITHDRAWAL_STATUS_MAP[wd.status] ? wd.status : 'pending'}
            statusMap={WITHDRAWAL_STATUS_MAP}
          />
          {wd.rejected_reason && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-1">{wd.rejected_reason}</p>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'วันที่',
      render: (wd) => (
        <span className="text-sm text-gray-400 dark:text-slate-500">{new Date(wd.created_at).toLocaleString('th-TH')}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (wd) => (
        <>
          {wd.status === 'pending' && (
            <button
              onClick={() => handleCancel(wd.id)}
              className="px-3 py-1 text-xs text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            >
              ยกเลิก
            </button>
          )}
          {wd.slip_url && (
            <a
              href={wd.slip_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 text-xs text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            >
              ดูสลิป
            </a>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faWallet}
        title="Wallet"
        subtitle="Commission wallet overview"
        actions={
          <UiButton icon={faMoneyBillTransfer} onClick={() => setShowWithdrawForm(!showWithdrawForm)}>
            ถอนเงิน
          </UiButton>
        }
      />

      {loadError && (
        <div className="mb-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-start gap-3">
          <FontAwesomeIcon icon={faBan} className="text-red-500 dark:text-red-400 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-red-700 dark:text-red-300">โหลดข้อมูล Wallet ไม่สำเร็จ</p>
            <p className="text-red-600/80 dark:text-red-400/80">ยอดถูกล้างเพื่อไม่ให้แสดงค่าค้างเก่า — กรุณารีเฟรชอีกครั้ง ({loadError})</p>
          </div>
        </div>
      )}

      {/* Wallet Summary — แถวหลัก: คงเหลือ / คอมรวม / ถอนจริง / อายัด (แยก freeze ออกจากยอดถอน) */}
      <UiStatsGrid cols={4} cards={walletCards} />

      {/* Commission แยก ฝาก(payin) / ถอน(payout) / Sub-Reseller spread */}
      <UiStatsGrid cols={3} cards={commissionCards} />

      {/* Withdrawals List */}
      {withdrawals.length > 0 && (
        <div className="mb-6">
          <DataTable<Withdrawal>
            data={withdrawals}
            columns={withdrawalColumns}
            keyExtractor={(wd) => wd.id}
            pagination={false}
            hoverable
            headerContent={
              <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">
                <FontAwesomeIcon icon={faMoneyBillTransfer} className="mr-2" style={{ color: 'var(--theme-primary)' }} />
                รายการถอนเงิน
              </h2>
            }
          />
        </div>
      )}

      {/* Transactions filter — fetch on explicit search */}
      <UiFilterPanel
        dateFrom={txStartDate}
        dateTo={txEndDate}
        onDateFromChange={setTxStartDate}
        onDateToChange={setTxEndDate}
        onSearch={() => loadAll(1)}
        onReset={txFilterActive ? () => { setTxTypeFilter('all'); setTxStartDate(''); setTxEndDate(''); loadAll(1, { resetFilter: true }); } : undefined}
        loading={loading}
      >
        <div className="flex-1 min-w-[200px] sm:max-w-[240px]">
          <UiSelect label="ประเภทรายการ" value={txTypeFilter} onChange={(e) => setTxTypeFilter(e.target.value)}>
            <option value="all">ทุกประเภท</option>
            <option value="commission">Commission</option>
            <option value="payout_commission">Payout Commission</option>
            <option value="parent_commission">Parent Spread (P.In)</option>
            <option value="parent_payout_commission">Parent Spread (P.Out)</option>
            <option value="withdrawal">ถอนเงิน</option>
            <option value="refund">คืนเงิน</option>
            <option value="freeze">Freeze</option>
            <option value="unfreeze_return">คืนเงิน Unfreeze</option>
          </UiSelect>
        </div>
      </UiFilterPanel>

      {/* Transactions */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Transactions</h2>
          <span className="text-xs text-gray-400 dark:text-slate-500">{txTotal} รายการ</span>
        </div>
        {!(data?.transactions?.length) ? (
          <div className="p-12 text-center">
            <FontAwesomeIcon icon={faCoins} className="text-4xl text-gray-300 dark:text-slate-600 mb-4" />
            <p className="text-gray-500 dark:text-slate-400">{txTypeFilter !== 'all' || txStartDate || txEndDate ? 'ไม่พบรายการที่ตรงกับเงื่อนไข' : 'No transactions yet'}</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {data.transactions.map((tx) => {
                const isCredit = TX_CREDIT_TYPES.includes(tx.transaction_type);
                const meta = TX_TYPE_META[tx.transaction_type] || { label: tx.transaction_type, color: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300' };
                return (
                  <div key={tx.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${txIconClasses(tx.transaction_type, isCredit)}`}>
                        <FontAwesomeIcon icon={isCredit ? faArrowUp : faArrowDown} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{tx.description}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.color}`}>
                            {meta.label}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-slate-500">{new Date(tx.created_at).toLocaleString('th-TH')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold tabular-nums ${isCredit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {isCredit ? '+' : '-'}฿{formatCurrency(tx.amount)}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-slate-500">Balance: ฿{formatCurrency(tx.balance_after)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Pagination
              currentPage={txPage}
              totalPages={Math.max(1, Math.ceil(txTotal / txPerPage))}
              totalItems={txTotal}
              pageSize={txPerPage}
              pageSizeOptions={[10, 20, 50, 100]}
              showPageSizeSelector
              showInfo
              showFirstLast={false}
              onPageChange={(p) => loadAll(p)}
              onPageSizeChange={(s) => { setTxPerPage(s); loadAll(1, { perPage: s }); }}
            />
          </>
        )}
      </div>

      {/* Withdrawal Form */}
      <UiModal
        open={showWithdrawForm}
        onClose={() => setShowWithdrawForm(false)}
        title="ถอนเงิน Commission"
        icon={faMoneyBillTransfer}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowWithdrawForm(false)} disabled={submitting} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton loading={submitting} onClick={() => withdrawFormRef.current?.requestSubmit()} className="flex-1 justify-center">
              {submitting ? 'กำลังส่ง...' : 'ส่งคำขอถอนเงิน'}
            </UiButton>
          </div>
        }
      >
        <form ref={withdrawFormRef} onSubmit={handleWithdraw} className="space-y-4">
          {/* Currency Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCurrency('THB')}
              className={`px-5 py-2 rounded-xl text-sm font-medium transition-colors ${
                currency === 'THB' ? 'text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
              style={currency === 'THB' ? { background: 'var(--theme-primary-gradient)' } : undefined}
            >
              🏦 THB (บัญชีธนาคาร)
            </button>
            <button
              type="button"
              onClick={() => setCurrency('USDT')}
              className={`px-5 py-2 rounded-xl text-sm font-medium transition-colors ${
                currency === 'USDT' ? 'text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
              style={currency === 'USDT' ? { background: 'var(--theme-primary-gradient)' } : undefined}
            >
              💲 USDT (Crypto)
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UiInput
              label="จำนวนเงิน (บาท)"
              required
              prefix="฿"
              type="number"
              step="0.01"
              min="1"
              max={data?.wallet?.balance || 0}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              hint={`คงเหลือ: ฿${formatCurrency(data?.wallet?.balance || 0)}`}
            />

            {currency === 'THB' ? (
              <>
                <UiSelect
                  label="ธนาคาร"
                  required
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                >
                  <option value="">เลือกธนาคาร</option>
                  {bankList.filter(b => b.isActive).map(b => (
                    <option key={b.bankCode} value={b.shortName}>{b.shortName} - {b.bankName}</option>
                  ))}
                </UiSelect>
                <UiInput
                  label="เลขบัญชี"
                  required
                  type="text"
                  value={form.bank_account_number}
                  onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })}
                  placeholder="เลขบัญชี 10-15 หลัก"
                />
                <UiInput
                  label="ชื่อบัญชี"
                  required
                  type="text"
                  value={form.bank_account_name}
                  onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })}
                  placeholder="ชื่อ-นามสกุล"
                />
              </>
            ) : (
              <>
                <UiSelect
                  label="Network"
                  value={form.network}
                  onChange={(e) => setForm({ ...form, network: e.target.value })}
                >
                  <option value="TRC20">TRC20 (Tron)</option>
                  <option value="ERC20">ERC20 (Ethereum)</option>
                  <option value="BEP20">BEP20 (BSC)</option>
                </UiSelect>
                <UiInput
                  label="Wallet Address"
                  required
                  type="text"
                  value={form.wallet_address}
                  onChange={(e) => setForm({ ...form, wallet_address: e.target.value })}
                  placeholder="เช่น TXxx...xxx"
                  className="font-mono"
                  wrapperClassName="md:col-span-2"
                />
                {/* USDT Preview */}
                {usdtLoading && (
                  <div className="md:col-span-2 text-center text-sm text-gray-400 dark:text-slate-500 py-2">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />กำลังดึงราคา USDT...
                  </div>
                )}
                {usdtPreview && !usdtLoading && (
                  <div className="md:col-span-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 space-y-2 text-sm">
                    <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase">USDT Conversion Summary</p>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-300">ยอดถอน (THB)</span>
                      <span className="font-medium text-gray-800 dark:text-slate-100 tabular-nums">฿{formatCurrency(usdtPreview.net_thb + usdtPreview.fee_amount)}</span>
                    </div>
                    <div className="flex justify-between text-red-600 dark:text-red-400">
                      <span>ค่าธรรมเนียม ({usdtPreview.fee_percent}%)</span>
                      <span className="font-medium tabular-nums">-฿{formatCurrency(usdtPreview.fee_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-300">หลังหัก Fee (THB)</span>
                      <span className="font-medium text-gray-800 dark:text-slate-100 tabular-nums">฿{formatCurrency(usdtPreview.net_thb)}</span>
                    </div>
                    <div className="flex justify-between text-gray-500 dark:text-slate-400">
                      <span>Rate</span>
                      <span>1 USDT = ฿{usdtPreview.rate.toLocaleString()}</span>
                    </div>
                    <hr className="border-amber-200 dark:border-amber-800" />
                    <div className="flex justify-between text-lg font-bold text-amber-700 dark:text-amber-300">
                      <span>จะได้รับ (USDT)</span>
                      <span className="tabular-nums">{usdtPreview.usdt_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT</span>
                    </div>
                  </div>
                )}
              </>
            )}

            <UiInput
              label="หมายเหตุ"
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="หมายเหตุ (ถ้ามี)"
              wrapperClassName="md:col-span-2"
            />
          </div>

        </form>
      </UiModal>
    </div>
  );
};

export default ResellerWalletPage;
